import React, { useMemo, useState, useCallback } from "react";
import { Box, List, Divider, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import MenuHeader from "../shared/menuHeader";
import MenuTabs from "../shared/menuTabs";
import MenuListItem from "../shared/menuListItem";
import CategoryMenuLevel2 from "../categoryMenuLevel2/categoryMenuLevel2";
import { MENU } from "../../../../../../locales/components/namespaces";

const CategoryMenu = ({ onClose, menuData = [], loading = false }) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("all");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [navigationStack, setNavigationStack] = useState([]);
  const { t, i18n } = useTranslation(MENU);

  const getCategoryLabel = useCallback(
      (item) => {
        return i18n.language?.toLowerCase() === "el"
            ? item?.nameEL || ""
            : item?.nameEN || item?.nameEL || "";
      },
      [i18n.language]
  );

  const getChildren = useCallback(
      (parentId) => {
        return menuData.filter(
            (item) => String(item.ParentId) === String(parentId)
        );
      },
      [menuData]
  );

  const rootCategories = useMemo(() => {
    return menuData.filter(
        (item) =>
            item.ParentId === null ||
            item.ParentId === undefined ||
            item.ParentId === ""
    );
  }, [menuData]);

  const tabs = useMemo(() => {
    return [
      { label: "ΌΛΑ", id: "all" },
      ...rootCategories.map((item) => ({
        label: getCategoryLabel(item),
        id: String(item.id),
      })),
    ];
  }, [rootCategories, getCategoryLabel]);

  const firstLevelCategories = useMemo(() => {
    if (activeTab === "all") {
      const rootIds = rootCategories.map((item) => String(item.id));

      return menuData.filter(
          (item) =>
              item.ParentId !== null &&
              item.ParentId !== undefined &&
              item.ParentId !== "" &&
              rootIds.includes(String(item.ParentId))
      );
    }

    return menuData.filter(
        (item) => String(item.ParentId) === String(activeTab)
    );
  }, [activeTab, menuData, rootCategories]);

  const currentParent = navigationStack[navigationStack.length - 1] || null;

  const currentCategories = useMemo(() => {
    if (!currentParent) {
      return firstLevelCategories;
    }

    return getChildren(currentParent.id);
  }, [currentParent, firstLevelCategories, getChildren]);

  const handleCategoryClick = (category) => {
    const children = getChildren(category.id);

    if (children.length > 0) {
      setNavigationStack((prev) => [...prev, category]);
      return;
    }

    history.push({
      pathname: "/products",
      state: {
        categoryId: category.id,
        categoryName: getCategoryLabel(category),
      },
    });
    onClose();
  };

  const handleBack = () => {
    setNavigationStack((prev) => prev.slice(0, -1));
  };

  const currentTitle = currentParent
      ? getCategoryLabel(currentParent)
      : t("PRODUCTS");

  const renderList = (items) => {
    if (items.length === 0) {
      return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">Δεν βρέθηκαν κατηγορίες</Typography>
          </Box>
      );
    }

    return items.map((category, index) => {
      const submenuExists = getChildren(category.id).length > 0;

      return (
          <React.Fragment key={category.id}>
            <Box
                onClick={() => handleCategoryClick(category)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                sx={{ cursor: "pointer" }}
            >
              <MenuListItem
                  icon={category.icon}
                  name={getCategoryLabel(category)}
                  isHighlight={false}
                  isHovered={hoveredIndex === index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  hasSubmenu={submenuExists}
              />
            </Box>

            {index < items.length - 1 && (
                <Divider
                    sx={{
                      backgroundColor: "#b9b9b9",
                      margin: "0px",
                    }}
                />
            )}
          </React.Fragment>
      );
    });
  };

  if (currentParent) {
    return (
        <CategoryMenuLevel2
            title={currentTitle}
            icon={currentParent.icon}
            items={currentCategories.map((item) => ({
              ...item,
              name: getCategoryLabel(item),
              icon: item.icon,
              hasSubmenu: getChildren(item.id).length > 0,
            }))}
            onBack={handleBack}
            onClose={onClose}
            onItemClick={handleCategoryClick}
        />
    );
  }

  return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <MenuHeader title={t("PRODUCTS")} onClose={onClose} />

        <MenuTabs
            brands={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setNavigationStack([]);
            }}
        />

        {loading ? (
            <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
            >
              <CircularProgress />
            </Box>
        ) : (
            <List
                sx={{
                  flex: 1,
                  overflow: "auto",
                  padding: 0,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
            >
              {renderList(currentCategories)}
            </List>
        )}
      </Box>
  );
};

export default CategoryMenu;